"use client";

import { useState } from "react";
import { Anchor, Box, Button, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { trackGizmo } from "@/lib/analytics";

type Swatch = { name: string; hex: string };

export default function PaletteReference({ colors, neutrals }: { colors: Swatch[]; neutrals: Swatch[] }) {
  const [status, setStatus] = useState("");
  async function copy(color: Swatch) {
    try {
      await navigator.clipboard.writeText(color.hex);
      setStatus(`${color.name}: ${color.hex} copied.`);
      trackGizmo("palette_hex_copied", { palette: "soft-autumn", color: color.name });
    } catch {
      setStatus(`Copy unavailable. Select and copy ${color.hex} below ${color.name}.`);
    }
  }

  return (
    <Box component="section" aria-labelledby="soft-autumn-reference" mt="xl" p={{ base: "sm", sm: "xl" }} bg="white" style={{ borderRadius: 12 }}>
      <Title order={2} id="soft-autumn-reference" size="h3" mb="sm">Soft Autumn swatches and hex codes</Title>
      <Text size="sm" c="dimmed" mb="lg">Save this warm, muted palette for shopping or planning outfits. Tap a color to copy its hex code. Screen and fabric colors can differ.</Text>
      <Box component="figure" m={0} mb="lg">
        <img
          src="/palette/autumn/soft/color-card.png"
          alt="Soft Autumn color palette with labeled muted gold, dusty coral, sage, soft rust, olive and warm neutral swatches with hex codes"
          width={1200}
          height={1030}
          loading="lazy"
          decoding="async"
          style={{ display: "block", width: "100%", height: "auto", borderRadius: 8 }}
        />
        <Text component="figcaption" size="sm" c="dimmed" mt="sm">
          Soft Autumn color card: warm, muted colors and everyday neutrals for outfit planning.
          Download the PNG below to keep the labeled swatches and hex codes handy. Screen, print and fabric colors can differ.
        </Text>
      </Box>
      {[["Palette colors", colors], ["Everyday neutrals", neutrals]].map(([label, swatches]) => (
        <Box key={label as string} mb="lg">
          <Title order={3} size="h5" mb="sm">{label as string}</Title>
          <SimpleGrid cols={{ base: 2, xs: 3, sm: 4 }} spacing="sm">
            {(swatches as Swatch[]).map(color => (
              <Stack key={color.name} gap={4}>
                <button type="button" onClick={() => copy(color)} aria-label={`Copy ${color.name} ${color.hex}`} style={{ cursor: "pointer", width: "100%", height: 76, background: color.hex, border: "1px solid #d4cec4", borderRadius: 8 }} />
                <Text size="sm" fw={500}>{color.name}</Text>
                <Text size="xs" component="code">{color.hex}</Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Box>
      ))}
      <Text role="status" aria-live="polite" size="sm" mih={24}>{status}</Text>
      <Button component="a" href="/palette/autumn/soft/color-card.png" download="soft-autumn-color-card.png" color="dark" onClick={() => trackGizmo("palette_card_download", { palette: "soft-autumn" })}>Download the color card (PNG)</Button>
      <Text mt="md" size="sm">Still comparing seasons? <Anchor href="/color-analysis-quiz">Take the free color season quiz</Anchor> or <Anchor href="/virtual-color-draping">compare colors on your photo</Anchor>.</Text>
    </Box>
  );
}
